/**
 * Speedtest engine constants.
 *
 * Every tunable value lives here — zero magic numbers in measurement code.
 * Values are exported individually so tree-shaking works and consumers
 * can override via SpeedtestConfig without importing the full set.
 *
 * Tuned for the full range of connections: 5 Mbps rural broadband to
 * 1 Gbps fiber. The navigator.connection auto-tuner further adapts
 * streams/chunks for 2G/3G connections.
 */

// ── Test phase durations ─────────────────────────────────────────────

/** Maximum download test duration (ms). */
export const DEFAULT_DOWNLOAD_DURATION_MS = 10_000;

/** Maximum upload test duration (ms). */
export const DEFAULT_UPLOAD_DURATION_MS = 10_000;

/** Grace period before download measurement begins (ms). TCP slow start completes in 100-500ms. */
export const DOWNLOAD_GRACE_TIME_MS = 1_000;

/** Grace period before upload measurement begins (ms). Allows buffer fill. */
export const UPLOAD_GRACE_TIME_MS = 2_000;

// ── Stream configuration ─────────────────────────────────────────────

/** Number of parallel download fetch streams. 4 is Ookla's lower bound; gentle on slow links. */
export const DEFAULT_DOWNLOAD_STREAMS = 4;

/** Number of parallel upload XHR streams. */
export const DEFAULT_UPLOAD_STREAMS = 4;

/**
 * Server-side data per download request (MB).
 * 10MB works across the range: 5 Mbps fills one request per stream in ~16s,
 * 100 Mbps cycles ~12 requests per stream in 10s.
 */
export const DEFAULT_CHUNK_SIZE_MB = 10;

/** Stagger delay between starting concurrent streams (ms). 200ms × 4 = 800ms total ramp. */
export const STREAM_STAGGER_DELAY_MS = 200;

// ── Measurement ──────────────────────────────────────────────────────

/** Sliding window duration for speed calculation (ms). */
export const SLIDING_WINDOW_MS = 3_000;

/** Interval between progress reports from worker to main thread (ms). */
export const PROGRESS_REPORT_INTERVAL_MS = 200;

/** Number of ping samples to collect. */
export const DEFAULT_PING_COUNT = 10;

/** Transport overhead compensation factor (TCP/IP headers, TLS). */
export const OVERHEAD_COMPENSATION_FACTOR = 1.06;

// ── Upload data generation ───────────────────────────────────────────

/**
 * Size of each upload blob (MB).
 * 8MB balances granularity with slow-link feasibility.
 * On 5 Mbps upload, takes ~13s (one per stream). On 100 Mbps, ~0.6s (good sample rate).
 */
export const UPLOAD_BLOB_SIZE_MB = 8;

/** Web Crypto API maximum bytes per `getRandomValues()` call. Spec-mandated limit. */
export const CRYPTO_RANDOM_MAX_BYTES = 65_536;

// ── Unit formatting thresholds ───────────────────────────────────────

/** Speed threshold above which we display Gbps instead of Mbps. */
export const GBPS_THRESHOLD_MBPS = 1_000;

/** Latency threshold above which we display seconds instead of ms. */
export const SECONDS_THRESHOLD_MS = 1_000;

// ── Derived byte constants ───────────────────────────────────────────

export const BYTES_PER_MEGABYTE = 1_000_000;
export const BITS_PER_MEGABIT = 1_000_000;

// ── Sample buffer ────────────────────────────────────────────────────

/** Ring buffer capacity for speed samples. Bounds memory during long tests. */
export const SAMPLE_BUFFER_CAPACITY = 2_000;
